import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React from 'react';
import { BrowserRouter } from 'react-router-dom';
import { Toaster } from 'sonner';

import { ConsentReacceptanceModal } from '@/components/consent/ConsentReacceptanceModal';
import { GlobalErrorBoundary } from '@/components/GlobalErrorBoundary';
import { ImpersonationBanner } from '@/components/ImpersonationBanner';
import { InstallPrompt } from '@/components/InstallPrompt';
import { WebNotificationsBootstrap } from '@/components/notifications/WebNotificationsBootstrap';
import { TooltipProvider } from '@/components/ui/tooltip';
import { AuthProvider } from '@/contexts/AuthContext';
import { ImpersonationProvider } from '@/contexts/ImpersonationContext';
import { LanguageProvider } from '@/contexts/LanguageContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { useIntercom } from '@/hooks/useIntercom';
import { AppRoutes } from '@/routes/AppRoutes';

/**
 * IntercomBootstrap — mounts inside AuthProvider so useIntercom can access
 * the authenticated user. Renders nothing; side-effects only.
 */
function IntercomBootstrap(): null {
  useIntercom();
  return null;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      // Exponential back-off: 1 s → 2 s → capped at 10 s.
      // Prevents hammering the server on slow mobile connections.
      retryDelay: (attemptIndex) => Math.min(1_000 * 2 ** attemptIndex, 10_000),
    },
  },
});

export default function App(): React.JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <LanguageProvider>
          <GlobalErrorBoundary>
            <BrowserRouter>
              <TooltipProvider delayDuration={300}>
                <AuthProvider>
                  <ImpersonationProvider>
                    <IntercomBootstrap />
                    <WebNotificationsBootstrap />
                    <ImpersonationBanner />
                    <AppRoutes />
                    <ConsentReacceptanceModal />
                    <InstallPrompt />
                  </ImpersonationProvider>
                </AuthProvider>
              </TooltipProvider>
            </BrowserRouter>
            <Toaster position="top-right" richColors />
          </GlobalErrorBoundary>
        </LanguageProvider>
      </ThemeProvider>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}
